import React, { useState } from 'react';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { SendOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { emailService } from '../../services/emailService';
import useTimezone from '../../hooks/useTimezone';
import type { EmailSettings } from '../../types/email';
import { SectionCard } from '../common/SectionCard';
import ConfirmationDialog from '../common/ConfirmationDialog';
import ConnectMailboxDialog from './ConnectMailboxDialog';
import SenderDetailsDialog from './SenderDetailsDialog';
import TestEmailDialog from './TestEmailDialog';

interface Props {
  settings: EmailSettings;
  writable: boolean;
  onChange: (next: EmailSettings) => void;
}

/** One fact about how emails go out: what it is, its value, and how to change it. */
const Row: React.FC<{ label: string; value: React.ReactNode; caption?: React.ReactNode; action?: React.ReactNode }> = ({ label, value, caption, action }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={{ xs: 0.5, sm: 2 }} sx={{ py: 1.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: { sm: 140 }, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>{value}</Typography>
      {caption && <Typography variant="caption" color="text.secondary" component="div">{caption}</Typography>}
    </Box>
    {action && <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>{action}</Stack>}
  </Stack>
);

const linkSx = { textTransform: 'none', fontWeight: 600 } as const;

/** How emails go out: the sender's name, where replies go, and the mailbox they're sent from. */
const EmailSenderCard: React.FC<Props> = ({ settings, writable, onChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { formatDate } = useTimezone();
  const [editing, setEditing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);

  const { own, platform, usage } = settings;
  const usingOwn = !!own && !own.failedAt;
  const provider = own && settings.providers.find(p => p.key === own.provider);
  const fromName = settings.fromName || settings.defaults.fromName;
  const replyTo = settings.replyTo || settings.defaults.replyTo;
  const address = usingOwn ? own!.username : platform.address;
  const canSend = usingOwn || platform.ready;

  const useEduBook = async () => {
    setBusy(true);
    try {
      onChange(await emailService.disconnectMailbox());
      setRemoving(false);
      enqueueSnackbar('Emails now go from EduBook mail', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Couldn’t remove the mailbox', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard
      title="How emails are sent"
      action={writable && canSend && (
        <Button size="small" startIcon={<SendOutlined sx={{ fontSize: 16 }} />} onClick={() => setTesting(true)} sx={linkSx}>
          Send a test
        </Button>
      )}
    >
      {own?.failedAt && (
        <Alert
          severity="error" sx={{ mb: 1, borderRadius: '10px' }}
          action={writable && <Button color="inherit" size="small" onClick={() => setConnecting(true)}>Reconnect</Button>}
        >
          {own.username} stopped working on {formatDate(own.failedAt)}: {own.lastError} Emails are going from EduBook mail until you reconnect it.
        </Alert>
      )}
      {!canSend && (
        <Alert severity="warning" sx={{ mb: 1, borderRadius: '10px' }}>
          EduBook mail isn’t set up on this server yet, so nothing can be sent. Connect your school’s own mailbox to start.
        </Alert>
      )}

      <Row
        label="From"
        value={fromName}
        caption={address}
        action={writable && <Button size="small" onClick={() => setEditing(true)} sx={linkSx}>Change</Button>}
      />
      <Divider />
      <Row
        label="Replies go to"
        value={replyTo || 'Not set'}
        caption="When a parent replies, it lands here."
        action={writable && <Button size="small" onClick={() => setEditing(true)} sx={linkSx}>Change</Button>}
      />
      <Divider />
      <Row
        label="Mailbox"
        value={usingOwn ? `Your own mailbox · ${own!.provider === 'other' ? own!.host : provider?.label}` : 'EduBook mail'}
        caption={canSend
          ? `${usingOwn ? `Connected ${formatDate(own!.verifiedAt)} · ` : 'No setup needed · '}${usage.sentToday} of ${usage.limit} sent in the last 24 hours`
          : 'Not available yet'}
        action={writable && (own ? (
          <>
            <Button size="small" onClick={() => setConnecting(true)} sx={linkSx}>Change</Button>
            <Button size="small" color="inherit" onClick={() => setRemoving(true)} sx={{ ...linkSx, color: 'text.secondary' }}>Use EduBook mail</Button>
          </>
        ) : (
          <Button size="small" onClick={() => setConnecting(true)} sx={linkSx}>Use your own</Button>
        ))}
      />

      {editing && <SenderDetailsDialog settings={settings} onClose={() => setEditing(false)} onSaved={(next) => { onChange(next); setEditing(false); }} />}
      {connecting && (
        <ConnectMailboxDialog
          providers={settings.providers}
          current={own}
          onClose={() => setConnecting(false)}
          onConnected={(next) => { onChange(next); setConnecting(false); }}
        />
      )}
      {testing && <TestEmailDialog onClose={() => setTesting(false)} />}
      <ConfirmationDialog
        open={removing}
        onClose={() => !busy && setRemoving(false)}
        onConfirm={useEduBook}
        title="Use EduBook mail instead?"
        message={`Emails will go from EduBook mail under ${fromName}, with replies still going to ${replyTo || 'your school'}. The saved password for ${own?.username ?? 'your mailbox'} is deleted.`}
        confirmText="Use EduBook mail"
        confirmColor="primary"
        loading={busy}
      />
    </SectionCard>
  );
};

export default EmailSenderCard;
